import React from 'react';
import type { DoorConfiguration } from '../../types/doorConfiguration';
import { Separator } from '../../ui/separator';
import {
  colourDetail,
  FRAME_LABELS,
  furnitureLabel,
  glassLabel,
  slabStyleLabel,
} from '../configurationLabels';
import { DOOR_DESIGNER_STAGES } from '../stages';

function Row({
  label,
  value,
  onEdit,
}: {
  label: string;
  value: string;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-3 text-sm">
      <div className="min-w-0">
        <p className="font-medium">{label}</p>
        <p className="mt-0.5 text-muted-foreground">{value}</p>
      </div>
      <button
        type="button"
        className="shrink-0 text-xs font-semibold uppercase tracking-wide text-primary hover:underline"
        onClick={onEdit}
      >
        Edit
      </button>
    </div>
  );
}

export const ReviewStage: React.FC<{
  configuration: DoorConfiguration;
  onEditStage: (index: number) => void;
}> = ({ configuration, onEditStage }) => {
  const size = `${configuration.dimensions.overallWidthMm} × ${configuration.dimensions.overallHeightMm} mm`;
  const frameSummary = `${FRAME_LABELS[configuration.frameProfile]} · ${size} · ${
    configuration.ancillaries.hasCill ? 'Cill' : 'No cill'
  }`;
  const colourSummary = [
    `Door ${colourDetail(configuration.slab.colourId)} / inside ${colourDetail(configuration.slab.internalColourId)}`,
    `Frame ${colourDetail(configuration.frameColourId)} / inside ${colourDetail(configuration.internalFrameColourId)}`,
    configuration.ancillaries.hasCill
      ? `Cill ${colourDetail(configuration.cillColourId)} / inside ${colourDetail(configuration.internalCillColourId)}`
      : null,
    configuration.ancillaries.hasThreshold
      ? `Threshold ${colourDetail(configuration.thresholdColourId)} / inside ${colourDetail(configuration.internalThresholdColourId)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="mb-2 text-sm text-muted-foreground">
        Quote integration is not wired yet. Edit a step to change the configuration.
      </p>
      <Row
        label={DOOR_DESIGNER_STAGES[0].stepperLabel}
        value={frameSummary}
        onEdit={() => onEditStage(0)}
      />
      <Separator />
      <Row
        label={DOOR_DESIGNER_STAGES[1].stepperLabel}
        value={`${slabStyleLabel(configuration.slab.styleId)} · ${
          configuration.slab.handing === 'left' ? 'Left' : 'Right'
        } handing`}
        onEdit={() => onEditStage(1)}
      />
      <Separator />
      <Row
        label={DOOR_DESIGNER_STAGES[2].stepperLabel}
        value={colourSummary}
        onEdit={() => onEditStage(2)}
      />
      <Separator />
      <Row
        label={DOOR_DESIGNER_STAGES[3].stepperLabel}
        value={glassLabel(configuration.glazing)}
        onEdit={() => onEditStage(3)}
      />
      <Separator />
      <Row
        label={DOOR_DESIGNER_STAGES[4].stepperLabel}
        value={furnitureLabel(configuration.hardware)}
        onEdit={() => onEditStage(4)}
      />
    </div>
  );
};
